import React, { FC, ReactNode } from 'react';

import {
  EditIcon,
  EyeIconSmall,
  SwitchVerticalIcon,
  ThreeDotsIcon,
  TrashBinIcon,
} from '../../../assets/icons/index';
import { AlertDialogWrapper } from '../../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/popover/index';
import { useModalState, useSharePlans } from '../../../hooks/index';
import { SharePlanUnionType } from '../../../types/pool-plans.types';
import { VestingTask } from '../../../types/vesting-tasks.types';
import { DeleteBlockModal } from '../delete-block-modal';
import { DeleteModal } from '../delete-modal';

export type PoolPlansPopoverProps = {
  canBeDeleted?: boolean;
  date: Date;
  id: string;
  isSharePlan?: boolean;
  isTerminated?: boolean;
  /** Pools only: opens the Adjust Pool Size flow. */
  onAdjustSizeClick?: (id: string) => void;
  onDeleteClick: (id: string) => void;
  onEditClick: (id: string) => void;
  onEditTerminationDateClick?: (id: string) => void;
  onViewClick: (id: string) => void;
  text: ReactNode | string;
} & (
  | { companyId?: never; sharePlans?: SharePlanUnionType[] | VestingTask[] }
  | { companyId?: string; sharePlans?: never }
);

export const PoolPlansPopover: FC<PoolPlansPopoverProps> = ({
  text,
  onAdjustSizeClick,
  onEditClick,
  onViewClick,
  onDeleteClick,
  onEditTerminationDateClick,
  id,
  isTerminated = false,
  canBeDeleted = true,
  companyId,
  sharePlans,
  isSharePlan,
  date,
}) => {
  const { handleCloseModal, handleSuccessModal, handleOpenModal, isOpen, toggler } = useModalState({
    onSuccess: () => onDeleteClick(id),
  });
  const { sharePlans: fetchedSharePlans } = useSharePlans({
    companyId: companyId || '',
    enabled: !!sharePlans,
    poolId: id || '',
    numberOfFetchedItems: 500,
    vestByDate: date,
  });

  return (
    <>
      <AlertDialogWrapper control={{ onOpenChange: toggler, open: isOpen }}>
        {canBeDeleted ? (
          <DeleteModal
            onClose={handleCloseModal}
            onSuccess={handleSuccessModal}
            text={text}
            warningMessageId={isSharePlan ? 'DELETE_SHARE_PLAN_DATA_TEMPLATES_WARNING' : undefined}
          />
        ) : (
          <DeleteBlockModal
            isSharePlan={isSharePlan}
            items={sharePlans || fetchedSharePlans}
            onClose={handleCloseModal}
          />
        )}
      </AlertDialogWrapper>

      <div className="flex cursor-pointer items-center justify-center">
        <Popover>
          <PopoverTrigger className="h-fit w-fit rounded data-[state=open]:shadow-md">
            <div className="flex size-6 rotate-90 items-center justify-center rounded bg-transparent transition-colors hover:bg-gray-100">
              <ThreeDotsIcon />
            </div>
          </PopoverTrigger>
          <PopoverContent className="flex w-fit flex-col gap-[2px] rounded p-2 font-normal shadow-2xl [&>*]:font-inter [&>*]:font-normal">
            <PopoverClose>
              {isTerminated && onEditTerminationDateClick ? (
                <div
                  className="flex w-full items-center gap-2 p-[6px] text-sm text-gray-700 transition-colors duration-200 hover:bg-gray-50 motion-reduce:transition-none"
                  data-permission-required
                  onClick={() => onEditTerminationDateClick(id)}
                >
                  <EditIcon className="size-4" />
                  <span className="text-sm font-normal text-gray-700">
                    <AppFormattedMessage id="EDIT_TERMINATION_DATE" />
                  </span>
                </div>
              ) : (
                <>
                  <div
                    className="flex w-full items-center gap-2 p-[6px] pr-4 text-sm text-gray-700 transition-colors duration-200 hover:bg-gray-50 motion-reduce:transition-none"
                    onClick={() => onViewClick(id)}
                  >
                    <EyeIconSmall className="size-4" />
                    <span className="text-sm font-normal text-gray-700">
                      <AppFormattedMessage
                        id="VIEW_POOL"
                        values={{
                          text,
                        }}
                      />
                    </span>
                  </div>
                  <div
                    className="flex w-full items-center gap-2 p-[6px] pr-4 text-sm text-gray-700 transition-colors duration-200 hover:bg-gray-50 motion-reduce:transition-none"
                    data-permission-required
                    onClick={() => onEditClick(id)}
                  >
                    <EditIcon className="size-4" />
                    <span className="text-sm font-normal text-gray-700">
                      <AppFormattedMessage
                        id="EDIT_POOL"
                        values={{
                          text,
                        }}
                      />
                    </span>
                  </div>
                  {onAdjustSizeClick && (
                    <div
                      className="flex w-full items-center gap-2 p-[6px] pr-4 text-sm text-gray-700 transition-colors duration-200 hover:bg-gray-50 motion-reduce:transition-none"
                      data-permission-required
                      onClick={() => onAdjustSizeClick(id)}
                    >
                      <SwitchVerticalIcon className="size-4" />
                      <span className="text-sm font-normal text-gray-700">
                        <AppFormattedMessage id="ADJUST_POOL_SIZE" />
                      </span>
                    </div>
                  )}
                  <div
                    className="flex w-full items-center gap-2 p-[6px] pr-4 text-sm text-fireside-500 transition-colors duration-200 hover:bg-gray-50 motion-reduce:transition-none"
                    data-permission-required
                    onClick={handleOpenModal}
                  >
                    <TrashBinIcon className="size-4" iconColor="#F04438" />
                    <span className="text-sm font-normal text-fireside-500">
                      <AppFormattedMessage
                        id="DELETE_POOL"
                        values={{
                          text,
                        }}
                      />
                    </span>
                  </div>
                </>
              )}
            </PopoverClose>
          </PopoverContent>
        </Popover>
      </div>
    </>
  );
};
